import { useEffect } from 'react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SITE = 'The Expat Studio';
export const DEFAULT_DESCRIPTION = 'Beautiful, comprehensive relocation guides for Australians moving to Europe, from visas and finances to small-town Italy.';

let configPromise = null;
export const loadSeoConfig = () => {
  if (!configPromise) configPromise = axios.get(`${BACKEND}/api/seo-config`).then((r) => r.data).catch(() => ({ pages: {} }));
  return configPromise;
};

const absolute = (url) => {
  if (!url) return `${window.location.origin}/og-logo.png`;
  if (url.startsWith('/api/')) return `${BACKEND}${url}`;
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
};

const setTag = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.rel = 'canonical'; document.head.appendChild(el); }
  el.href = href;
};

export const setJsonLd = (id, data) => {
  document.getElementById(`ld-${id}`)?.remove();
  if (!data) return;
  const el = document.createElement('script');
  el.type = 'application/ld+json'; el.id = `ld-${id}`; el.text = JSON.stringify(data);
  document.head.appendChild(el);
};

export const applySeo = ({ title, description, image, type = 'website', jsonLd = null }) => {
  const fullTitle = !title ? `${SITE} | Relocation Guides for Australians Moving to Europe` : title.includes(SITE) ? title : `${title} | ${SITE}`;
  const desc = (description || DEFAULT_DESCRIPTION).replace(/\s+/g, ' ').trim().slice(0, 160);
  const url = `${window.location.origin}${window.location.pathname}`;
  document.title = fullTitle;
  setTag('name', 'description', desc);
  setCanonical(url);
  [['og:title', fullTitle], ['og:description', desc], ['og:image', absolute(image)], ['og:url', url], ['og:type', type], ['og:site_name', SITE]].forEach(([k, v]) => setTag('property', k, v));
  [['twitter:card', 'summary_large_image'], ['twitter:title', fullTitle], ['twitter:description', desc], ['twitter:image', absolute(image)]].forEach(([k, v]) => setTag('name', k, v));
  setJsonLd('page', jsonLd);
};

// Set SEO for a page once its data is ready (pass null to wait).
export const useSeo = (seo, deps) => {
  useEffect(() => { if (seo) applySeo(seo); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

// Main pages whose title/description are editable in Admin → Settings.
export const usePageSeo = (key, defaults) => {
  useEffect(() => {
    applySeo(defaults);
    loadSeoConfig().then((c) => {
      const p = c.pages?.[key] || {};
      if (p.title || p.description) applySeo({ ...defaults, title: p.title || defaults.title, description: p.description || defaults.description });
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const productLd = ({ name, description, image, url, offers, sku }) => ({
  '@context': 'https://schema.org', '@type': 'Product', name, description, image: image ? [absolute(image)] : undefined, sku,
  brand: { '@type': 'Brand', name: SITE },
  offers: offers.map((o) => ({ '@type': 'Offer', name: o.name, price: Number(o.price).toFixed(2), priceCurrency: 'AUD', availability: 'https://schema.org/InStock', url: url || window.location.href })),
});

export const organizationLd = (c) => ({
  '@context': 'https://schema.org', '@type': 'Organization', name: c.site_name || SITE, url: window.location.origin,
  logo: `${window.location.origin}/favicon-512.png`, email: c.contact_email || undefined, sameAs: c.socials || [],
  description: DEFAULT_DESCRIPTION,
});
